import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import ImageLightbox from "./ImageLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REACTION_SET = ["❤️", "🙌", "🤔", "👀"];

/**
 * Native Folk moodboard. Multiple named boards per submission, upload-only pins,
 * per-pin reactions (emoji + short note + priority star).
 * Replaces the Pinterest embed entirely.
 */
export default function FolkMoodboard({ submissionId }) {
  const [boards, setBoards] = useState([]);
  const [role, setRole] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/${submissionId}/moodboards`, { withCredentials: true });
      setBoards(data.boards || []);
      setRole(data.role);
      setActiveId((prev) => prev || (data.boards?.[0]?.id ?? null));
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error("Sign in to see your moodboard.");
      } else {
        toast.error("Couldn't load your moodboards.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [submissionId]);

  const addBoard = async () => {
    const name = newBoardName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/portal/${submissionId}/moodboards`, { name }, { withCredentials: true });
      setNewBoardName("");
      setBoards((prev) => [...prev, data]);
      setActiveId(data.id);
      toast.success(`Board "${name}" created`);
    } catch (e) {
      toast.error("Couldn't create the board.");
    } finally {
      setCreating(false);
    }
  };

  const removeBoard = async (b) => {
    if (!window.confirm(`Delete board "${b.name}" and all its pins?`)) return;
    try {
      await axios.delete(`${API}/portal/${submissionId}/moodboards/${b.id}`, { withCredentials: true });
      setBoards((prev) => {
        const next = prev.filter((x) => x.id !== b.id);
        if (activeId === b.id) setActiveId(next[0]?.id || null);
        return next;
      });
      toast.success("Board removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onPinAdded = (pin) => {
    setBoards((prev) => prev.map((b) => b.id === pin.board_id ? { ...b, pins: [pin, ...(b.pins || [])] } : b));
  };
  const onPinRemoved = (pin) => {
    setBoards((prev) => prev.map((b) => b.id === pin.board_id ? { ...b, pins: (b.pins || []).filter((p) => p.id !== pin.id) } : b));
  };
  const onPinUpdated = (pin) => {
    setBoards((prev) => prev.map((b) => b.id === pin.board_id ? { ...b, pins: (b.pins || []).map((p) => p.id === pin.id ? pin : p) } : b));
  };

  if (loading) return <div style={{ color: "var(--folk-muted)" }}>Loading your boards…</div>;

  const active = boards.find((b) => b.id === activeId) || boards[0];

  return (
    <div data-testid="folk-moodboard" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", borderBottom: "1px solid var(--folk-border)", paddingBottom: 12 }}>
        {boards.map((b) => (
          <button
            key={b.id}
            type="button"
            data-testid={`board-tab-${b.id}`}
            onClick={() => setActiveId(b.id)}
            style={{
              padding: "8px 16px", borderRadius: 999,
              border: `1.5px solid ${activeId === b.id ? "var(--folk-accent)" : "var(--folk-border)"}`,
              background: activeId === b.id ? "var(--folk-accent)" : "var(--folk-card)",
              color: activeId === b.id ? "#FFFFFF" : "#2A2828",
              fontSize: 13.5, fontFamily: "'Host Grotesk', sans-serif",
              cursor: "pointer", fontWeight: activeId === b.id ? 600 : 400,
            }}
          >
            {b.name}
            <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>{(b.pins || []).length}</span>
          </button>
        ))}
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: "auto" }}>
          <input
            data-testid="new-board-input"
            className="folk-input"
            placeholder="New board name…"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addBoard()}
            style={{ maxWidth: 180, padding: "8px 12px", fontSize: 13 }}
          />
          <button data-testid="add-board-btn" type="button" onClick={addBoard} disabled={creating || !newBoardName.trim()} className="folk-btn" style={{ opacity: (creating || !newBoardName.trim()) ? 0.6 : 1 }}>
            {creating ? "…" : "+ Board"}
          </button>
        </div>
      </div>

      {active && (
        <BoardView
          key={active.id}
          board={active}
          role={role}
          submissionId={submissionId}
          onPinAdded={onPinAdded}
          onPinRemoved={onPinRemoved}
          onPinUpdated={onPinUpdated}
          onDeleteBoard={role === "admin" ? () => removeBoard(active) : null}
          onBoardReload={reload}
        />
      )}
      {!active && <div style={{ color: "var(--folk-muted)" }}>No boards yet. Create one above.</div>}
    </div>
  );
}

function BoardView({ board, role, submissionId, onPinAdded, onPinRemoved, onPinUpdated, onDeleteBoard, onBoardReload }) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await axios.post(`${API}/portal/${submissionId}/moodboards/${board.id}/pins`, form, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        onPinAdded(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const syncFromPinterest = async () => {
    const url = window.prompt("Paste a public Pinterest board URL. We'll pull up to 30 pins.");
    if (!url) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(
        `${API}/portal/${submissionId}/moodboards/${board.id}/pinterest-sync`,
        { url, limit: 30 },
        { withCredentials: true },
      );
      if (data?.imported > 0) {
        toast.success(`Pulled ${data.imported} pin${data.imported === 1 ? "" : "s"} from Pinterest.`);
        onBoardReload && onBoardReload();
      } else {
        toast.warning(data?.hint || "Couldn't read that Pinterest board. Add pins manually.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pinterest sync failed.");
    } finally { setSyncing(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 18 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid={`pin-upload-${board.id}`}
          style={{ display: "none" }}
        />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`pin-upload-btn-${board.id}`} className="folk-pill" style={{ opacity: busy ? 0.6 : 1 }}>
          {busy ? "Uploading…" : "+ Add pin"}
        </button>
        {role === "admin" && (
          <button
            type="button"
            onClick={syncFromPinterest}
            disabled={syncing}
            data-testid={`pinterest-sync-${board.id}`}
            className="folk-pill"
            style={{ background: "transparent", border: "1.5px solid #0F3A54", color: "#0F3A54", opacity: syncing ? 0.55 : 1 }}
          >
            {syncing ? "Syncing…" : "Sync from Pinterest"}
          </button>
        )}
        <div style={{ color: "var(--folk-muted)", fontSize: 13 }}>
          {(board.pins || []).length} pin{(board.pins || []).length === 1 ? "" : "s"} on <b>{board.name}</b>
        </div>
        {onDeleteBoard && (
          <button type="button" onClick={onDeleteBoard} data-testid={`delete-board-${board.id}`} className="folk-btn" style={{ marginLeft: "auto", color: "#B04A2E" }}>
            Delete board
          </button>
        )}
      </div>

      {(board.pins || []).length === 0 ? (
        <div style={{ padding: "36px 22px", background: "#FFFBC9", border: "2px dashed var(--folk-accent)", borderRadius: 16, textAlign: "center", color: "#68280E" }}>
          <div style={{ fontSize: 30 }}>🖼️</div>
          <div className="folk-serif" style={{ fontSize: 20, marginTop: 6 }}>this board is empty.</div>
          <div style={{ fontSize: 13.5, color: "#2A2828", marginTop: 6 }}>
            {role === "admin" ? "Add pins to build the visual language for this client." : "Add your first inspiration image using + Add pin above."}
          </div>
        </div>
      ) : (
        <div className="folk-moodboard-grid">
          {(board.pins || []).map((p) => (
            <PinCard
              key={p.id}
              pin={p}
              role={role}
              submissionId={submissionId}
              backend={backend}
              onRemove={onPinRemoved}
              onUpdate={onPinUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PinCard({ pin, role, submissionId, backend, onRemove, onUpdate }) {
  const [noteDraft, setNoteDraft] = useState("");
  const [zoomed, setZoomed] = useState(false);
  const myReaction = (pin.reactions || []).find((r) => r.by === role) || {};
  const teamReaction = (pin.reactions || []).find((r) => r.by !== role) || {};

  const react = async (body) => {
    try {
      const { data } = await axios.post(`${API}/portal/${submissionId}/moodboards/${pin.board_id}/pins/${pin.id}/react`, body, { withCredentials: true });
      onUpdate(data);
    } catch (e) {
      toast.error("Couldn't save reaction");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this pin?")) return;
    try {
      await axios.delete(`${API}/portal/${submissionId}/moodboards/${pin.board_id}/pins/${pin.id}`, { withCredentials: true });
      onRemove(pin);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const saveNote = async () => {
    if (noteDraft.trim() === (myReaction.note || "")) return;
    await react({ note: noteDraft });
  };

  const priorityCount = (pin.reactions || []).filter((r) => r.is_priority).length;
  const isPriority = !!myReaction.is_priority || priorityCount > 0;

  return (
    <div data-testid={`pin-${pin.id}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ position: "relative", background: "#F3ECE9" }}>
        <img
          src={pin.image_url?.startsWith("http") ? pin.image_url : `${backend}${pin.image_url}`}
          alt=""
          onClick={() => setZoomed(true)}
          data-testid={`pin-image-${pin.id}`}
          style={{ width: "100%", height: "auto", display: "block", objectFit: "cover", cursor: "zoom-in" }}
          loading="lazy"
        />
        {zoomed && (
          <ImageLightbox
            src={pin.image_url?.startsWith("http") ? pin.image_url : `${backend}${pin.image_url}`}
            alt={pin.note || ""}
            onClose={() => setZoomed(false)}
          />
        )}
        {isPriority && (
          <div title="Priority pin" style={{ position: "absolute", top: 10, right: 10, background: "#FFFBC9", color: "#68280E", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 600, border: "1.5px solid #68280E" }}>
            ⭐ Priority
          </div>
        )}
      </div>
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {REACTION_SET.map((emo) => (
            <button
              key={emo}
              type="button"
              data-testid={`react-${pin.id}-${emo}`}
              onClick={() => react({ emoji: emo })}
              style={{
                border: `1.5px solid ${myReaction.emoji === emo ? "var(--folk-accent)" : "var(--folk-border)"}`,
                background: myReaction.emoji === emo ? "#FFFBC9" : "transparent",
                borderRadius: 999, padding: "4px 10px", fontSize: 15, cursor: "pointer",
              }}
              aria-label={`react ${emo}`}
            >
              {emo}
            </button>
          ))}
          <button
            type="button"
            data-testid={`priority-${pin.id}`}
            onClick={() => react({ is_priority: !myReaction.is_priority })}
            title="Mark as a design priority"
            style={{
              marginLeft: "auto",
              border: `1.5px solid ${myReaction.is_priority ? "var(--folk-accent)" : "var(--folk-border)"}`,
              background: myReaction.is_priority ? "#FFFBC9" : "transparent",
              borderRadius: 999, padding: "4px 10px", fontSize: 13, cursor: "pointer", fontWeight: 600, color: "#68280E",
            }}
          >
            ⭐ {myReaction.is_priority ? "Priority" : "Mark priority"}
          </button>
        </div>

        <input
          className="folk-input"
          data-testid={`note-${pin.id}`}
          placeholder={myReaction.note ? "Update note…" : "Short note (e.g. love the wood tone)"}
          defaultValue={myReaction.note || ""}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={saveNote}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          style={{ padding: "8px 12px", fontSize: 13.5 }}
        />

        {teamReaction.note && (
          <div style={{ background: "#BADDF0", color: "#0F3A54", borderRadius: 10, padding: "8px 12px", fontSize: 13 }}>
            <b style={{ textTransform: "capitalize" }}>{teamReaction.by}:</b> {teamReaction.note}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--folk-muted)" }}>
          <span>Added by <b style={{ textTransform: "capitalize" }}>{pin.uploaded_by}</b></span>
          {(role === "admin" || pin.uploaded_by === role) && (
            <button type="button" onClick={remove} data-testid={`remove-pin-${pin.id}`} style={{ background: "none", border: "none", color: "#B04A2E", cursor: "pointer", fontSize: 12 }}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
